import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";

import PropTypes from "prop-types";
import { Checkbox } from "semantic-ui-react";

import { isNonEmptyArray } from "@cayuse/shared-react.utils.type-checks";

import { useEditingContext } from "components/BudgetBuilder/shared/EditingContext";
import { useTenantFormatter } from "components/BudgetBuilder/shared/TenantFormatter";
import Subsection from "components/Subsection";
import Table from "components/Table";
import { useDictionary } from "hooks";
import {
	saveBudgetBuilderForm,
	updatePeriodData,
	updateResearchTeam,
} from "store/actions/budgetBuilder";
import { budgetBuilderSelectors } from "store/selectors";
import {
	getEffortError,
	getPeriodSubtotal,
	getPersonMonthsError,
	getRequestedFringeSubtotal,
	getRequestedSalarySubtotal,
	isPresent,
	toNumber,
} from "utils/budgetBuilder";

import { ResearchButtons } from "../ResearchTeam";
import styles from "../ResearchTeam/styles.css";
import AddMemberModal from "./AddMemberModal";
import ResearchTeamRow from "./ResearchTeamRow";

const ResearchTeamList = ({ isCostShare }) => {
	const { currencyFormatter } = useTenantFormatter();
	const { researchTeam: [ isEditing, setIsEditing ] } = useEditingContext();
	const currentPeriodData = useSelector(budgetBuilderSelectors.currentPeriodData(isCostShare));
	const currentPeriodNumber = useSelector(budgetBuilderSelectors.currentPeriodNumber);
	const [ hasFringeRate, setHasFringeRate ] = useState(false);
	const handleHasFringeRateChange = () => {
		if (isEditing) {
			setHasFringeRate(!hasFringeRate);
			setIsModified(true);
		}
	};
	const [ hasSalaryRate, setHasSalaryRate ] = useState(false);
	const handleHasSalaryRateChange = () => {
		if (isEditing) {
			setHasSalaryRate(!hasSalaryRate);
			setIsModified(true);
		}
	};
	const faRateAmount = useSelector(budgetBuilderSelectors.currentPeriodRate);
	const [ addSponsorCostsModalOpen, setAddSponsorCostsModalOpen ] = useState(false);
	const [ isModified, setIsModified ] = useState(false);

	const savedMembers = useSelector(budgetBuilderSelectors.researchTeamMembers);
	const [ members, setMembers ] = useState([]);
	const savedUnnamedMembers = useSelector(budgetBuilderSelectors.unnamedMembers);
	const [ unnamedMembers, setUnnamedMembers ] = useState([]);

	useEffect(() => {
		setMembers(savedMembers);
		setUnnamedMembers(savedUnnamedMembers);
	}, [ currentPeriodNumber, savedMembers, savedUnnamedMembers ]);

	const handleReset = useCallback(() => {
		setMembers(savedMembers);
		setHasSalaryRate(currentPeriodData?.hasSalaryRate || false);
		setHasFringeRate(currentPeriodData?.hasFringeRate || false);
		setUnnamedMembers(savedUnnamedMembers);
		setIsModified(false);
		setIsEditing(false);
	}, [ currentPeriodData, savedMembers, savedUnnamedMembers, setIsEditing ]);

	const prevPeriodNumber = useRef(0);

	useEffect(() => {
		if (isPresent(currentPeriodNumber) && prevPeriodNumber.current !== currentPeriodNumber) {
			handleReset();
			prevPeriodNumber.current = currentPeriodNumber;
		}
	}, [ currentPeriodNumber ]);

	const handleSetMembers = newMembers => {
		setIsModified(true);
		setMembers(newMembers);
	};
	const setAllMembers = (newMembers, newUnnamedMembers) => {
		setIsModified(true);
		setMembers(newMembers);
		setUnnamedMembers(newUnnamedMembers);
	};

	const dictionary = useDictionary("budgetBuilder.components.ResearchTeamList");

	const handleSave = () => {
		setIsEditing(false);
		updateResearchTeam(members, unnamedMembers);
		updatePeriodData({ hasFringeRate, hasSalaryRate }, isCostShare);
		saveBudgetBuilderForm();
	};

	const handleRemoveItem = memberId => {
		const filterFunction = unnamedMember => unnamedMember.member !== memberId;
		const newResearchTeam = [ ...members.filter(filterFunction) ];

		handleSetMembers(newResearchTeam);
	};

	const handleRemoveUnnamedMember = memberId => {
		let newUnnamedMembers = [ ...unnamedMembers ];
		for (let i = 0; i < newUnnamedMembers.length; i++) {
			const unnamedMember = newUnnamedMembers[i];
			if (unnamedMember.id === memberId) {
				const excludedFromPeriods = unnamedMember?.excludedFromPeriods || [];
				newUnnamedMembers[i] = {
					...unnamedMember,
					excludedFromPeriods: [
						...excludedFromPeriods,
						currentPeriodNumber,
					],
				};
				break;
			}
		}
		const newResearchTeam = [ ...members.filter(member => member.id !== memberId) ];
		setAllMembers(newResearchTeam, newUnnamedMembers);
	};

	const updateResearchTeamMember = newMember => {
		let newMembers = [ ...members ];
		for (let i = 0; i < newMembers.length; i++) {
			let member = newMembers[i];
			if (member.id === newMember.id) {
				newMembers[i] = newMember;
				break;
			}
		}
		handleSetMembers(newMembers);
	};

	const requestedFringeSubtotal = useMemo(
		() => getRequestedFringeSubtotal(members, isCostShare),
		[ isCostShare, members ],
	);

	const fringeSubtotal = useMemo(() => {
		if (hasFringeRate) {
			return requestedFringeSubtotal * (toNumber(faRateAmount) / 100);
		}

		return 0;
	}, [ hasFringeRate, requestedFringeSubtotal, faRateAmount ]);

	const requestedSalarySubtotal = useMemo(
		() => getRequestedSalarySubtotal(members, isCostShare),
		[ isCostShare, members ]
	);

	const salaryRate = useMemo(() => {
		if (hasSalaryRate) {
			return requestedSalarySubtotal * (toNumber(faRateAmount) / 100);
		}

		return 0;
	}, [ faRateAmount, hasSalaryRate, requestedSalarySubtotal ]);

	const rateSubtotal = useMemo(
		() => fringeSubtotal + salaryRate,
		[ fringeSubtotal, salaryRate ],
	);

	const fringeAndSalarySubtotal = useMemo(
		() => getPeriodSubtotal(members, isCostShare),
		[ isCostShare, members ]
	);

	const periodSubtotal = useMemo(
		() => {
			if (!isCostShare) {
				return rateSubtotal + fringeAndSalarySubtotal;
			}

			return fringeAndSalarySubtotal;
		},
		[ fringeAndSalarySubtotal, isCostShare, rateSubtotal ],
	);

	const isValid = useMemo(
		() => {
			const updateKey = isCostShare ? "costShare" : "sponsoredCosts";
			for (let member of members) {
				const effort = member?.[updateKey]?.effort;
				const effortError = getEffortError(effort);
				if (effortError) {
					return false;
				}
				const personMonths = member?.[updateKey]?.personMonths;
				const personMonthsError = getPersonMonthsError(personMonths);
				if (personMonthsError) {
					return false;
				}
			}

			return true;
		},
		[ isCostShare, members ],
	);

	return (

		<Subsection
			title={"Research Team"}
			buttons={ResearchButtons({
				isEditing,
				isModified,
				setIsEditing,
				handleReset,
				handleSave,
				setAddSponsorCostsModalOpen,
				isValid,
			})}
		>
			<AddMemberModal
				isOpen={addSponsorCostsModalOpen}
				onClose={() => setAddSponsorCostsModalOpen(false)}
			/>
			<Table captionText="Research Team">
				<Table.Header>
					<Table.Row>
						<Table.HeaderCell className={styles.cellAlignLeft}>
							{dictionary.name || "Name"}
						</Table.HeaderCell>
						<Table.HeaderCell className={styles.cellAlignLeft}>
							{dictionary.role || "Role"}
						</Table.HeaderCell>
						<Table.HeaderCell className={styles.cellAlignLeft}>
							{dictionary.internalAssociation || "Internal Association"}
						</Table.HeaderCell>
						<Table.HeaderCell className={styles.cellAlignRight}>
							{dictionary.baseSalary || "Base Salary"}
						</Table.HeaderCell>
						<Table.HeaderCell className={styles.cellAlignRight}>
							{dictionary.apptMonths || "Appt Months"}
						</Table.HeaderCell>
						<Table.HeaderCell className={styles.cellAlignRight}>
							{dictionary.effort || "Effort"}
						</Table.HeaderCell>
						<Table.HeaderCell className={styles.cellAlignRight}>
							{dictionary.personMonths || "Person Months"}
						</Table.HeaderCell>
						<Table.HeaderCell className={styles.cellAlignRight}>
							{dictionary.requestedSalary || "Requested Salary"}
						</Table.HeaderCell>
						<Table.HeaderCell className={styles.cellAlignRight}>
							{dictionary.fringeRate || "Fringe Rate"}
						</Table.HeaderCell>
						<Table.HeaderCell className={styles.cellAlignRight}>
							{dictionary.requestedFringe || "Requested Fringe"}
						</Table.HeaderCell>
						<Table.HeaderCell className={styles.cellAlignRight}>
							{dictionary.periodTotal || "Period Total"}
						</Table.HeaderCell>
						<Table.HeaderCell />
					</Table.Row>
				</Table.Header>
				<Table.Body>
					{isNonEmptyArray(members) && members
						.sort((a, b) => a.sort > b.sort)
						.map((category, index) => {
							const isAddedMember = category.type !== "PARENT";

							return (
								<Fragment key={`category-${index}`}>
									<ResearchTeamRow
										category={category}
										handleRemoveItem={handleRemoveItem}
										handleRemoveUnnamedMember={handleRemoveUnnamedMember}
										hasFringeRate={hasFringeRate}
										isCostShare={isCostShare}
										isEditing={isEditing}
										isAddedMember={isAddedMember}
										position={index}
										updateResearchTeamMember={updateResearchTeamMember}
									/>
								</Fragment>
							);
						})
					}
					<Table.Row className={styles.rowHeader}>
						<Table.Cell className={styles.rowHeaderCell} colSpan={7}>
							<span className={styles.bold}>{"Research Team Salary and Fringe Subtotal"}</span>
						</Table.Cell>
						<Table.Cell
							className={`${styles.rowHeaderCell} ${styles.cellAlignRight} ${styles.valueCellPadding}`}
						>
							<span className={styles.bold}>
								{currencyFormatter(requestedSalarySubtotal)}
							</span>
						</Table.Cell>
						<Table.Cell className={styles.rowHeaderCell}>
							{""}
						</Table.Cell>
						<Table.Cell
							className={`${styles.rowHeaderCell} ${styles.cellAlignRight} ${styles.valueCellPadding}`}
						>
							<span className={styles.bold}>
								{currencyFormatter(requestedFringeSubtotal)}
							</span>
						</Table.Cell>
						<Table.Cell
							className={`${styles.rowHeaderCell} ${styles.cellAlignRight} ${styles.valueCellPadding}`}
						>
							<span className={styles.bold}>
								{currencyFormatter(fringeAndSalarySubtotal)}
							</span>
						</Table.Cell>
						<Table.Cell className={styles.rowHeaderCell}>
							{""}
						</Table.Cell>
					</Table.Row>
					<Table.Row>
						<Table.Cell colSpan="10">
							<div className={styles.horizontalCell}>
								<div className={styles.labelMinWidth}>
									<span className={styles.bold}>{"Salary"}</span>
								</div>
								<div className={styles.checkboxOffset}>
									<Checkbox
										label={`Include F&A rate (${faRateAmount}%)`}
										disabled={!isEditing}
										checked={hasSalaryRate}
										onClick={handleHasSalaryRateChange}
									/>
								</div>
							</div>
						</Table.Cell>
						<Table.Cell className={`${styles.cellAlignRight} ${styles.valueCellPadding}`}>
							<span>{currencyFormatter(salaryRate)}</span>
						</Table.Cell>
						<Table.Cell>
							{""}
						</Table.Cell>
					</Table.Row>
					<Table.Row>
						<Table.Cell colSpan={10}>
							<div className={styles.horizontalCell}>
								<div className={styles.labelMinWidth}>
									<span className={styles.bold}>{"Fringe"}</span>
								</div>
								<div className={styles.checkboxOffset}>
									<Checkbox
										label={`Include F&A rate (${faRateAmount}%)`}
										disabled={!isEditing}
										checked={hasFringeRate}
										onClick={handleHasFringeRateChange}
									/>
								</div>
							</div>
						</Table.Cell>
						<Table.Cell className={`${styles.cellAlignRight} ${styles.valueCellPadding}`}>
							<span>{currencyFormatter(fringeSubtotal)}</span>
						</Table.Cell>
						<Table.Cell>
							{""}
						</Table.Cell>
					</Table.Row>
					<Table.Row className={styles.rowHeader}>
						<Table.Cell className={styles.rowHeaderCell} colSpan={10}>
							<span className={styles.bold}>{"Research Team F&A Subtotal"}</span>
						</Table.Cell>
						<Table.Cell
							className={`${styles.rowHeaderCell} ${styles.cellAlignRight} ${styles.valueCellPadding}`}
						>
							<span className={styles.bold}>
								{currencyFormatter(rateSubtotal)}
							</span>
						</Table.Cell>
						<Table.Cell className={styles.rowHeaderCell}>
							{""}
						</Table.Cell>
					</Table.Row>
					<Table.Row className={styles.rowHeader}>
						<Table.Cell className={styles.rowHeaderCell} colSpan={10}>
							<span className={styles.bold}>{"Research Team Total"}</span>
						</Table.Cell>
						<Table.Cell className={`${styles.rowHeaderCell} ${styles.cellAlignRight} ${styles.valueCellPadding}`}>
							<span className={styles.bold}>{currencyFormatter(periodSubtotal)}</span>
						</Table.Cell>
						<Table.Cell className={styles.rowHeaderCell}>
							{""}
						</Table.Cell>
					</Table.Row>

				</Table.Body>
			</Table>

		</Subsection>

	);
};

ResearchTeamList.defaultProps = {
	isCostShare: false,
};
ResearchTeamList.propTypes = {
	isCostShare: PropTypes.bool,
};

export default ResearchTeamList;
